import type { NavItem, ProfilZiel } from '../types';
import type { DiktatModul } from './diktat-api';

let _nav: ((screen: NavItem) => void) | null = null;

export function registerNav(fn: ((screen: NavItem) => void) | null) {
  _nav = fn;
}

export function globalNav(screen: NavItem) {
  _nav?.(screen);
}

// Ausflug-Detail-Öffner (Welle M8e): von Kalender + Dashboard-Card genutzt.
// Welle U5: herkunft = Ziel von "← Zurück" (ohne Angabe: Kalender);
// 'behalten' lässt die bisherige Herkunft stehen (Rückkehr aus dem Bearbeiten).
// Welle U6b: ein ProfilHerkunft-Objekt führt zurück in genau dieses Profil (+ Tab).
export type AusflugHerkunft = NavItem | 'behalten' | ProfilHerkunft;
let _ausflugOpener: ((terminId: number, herkunft?: AusflugHerkunft) => void) | null = null;

export function registerAusflugOpener(fn: ((terminId: number, herkunft?: AusflugHerkunft) => void) | null) {
  _ausflugOpener = fn;
}

export function openAusflugDetail(terminId: number, herkunft?: AusflugHerkunft) {
  _ausflugOpener?.(terminId, herkunft);
}

// Welle U5: bestehenden Termin-Dialog (KalenderAnsicht) für genau diesen Termin
// öffnen. zurueck wird nach dem Schließen/Speichern aufgerufen (nicht nach Löschen).
let _terminZiel: { terminId: number; zurueck: (() => void) | null } | null = null;

export function oeffneTerminBearbeiten(terminId: number, zurueck: (() => void) | null = null) {
  _terminZiel = { terminId, zurueck };
  globalNav('kalender');
}

export function nimmTerminZiel(): { terminId: number; zurueck: (() => void) | null } | null {
  const z = _terminZiel;
  _terminZiel = null;
  return z;
}

// Welle M1b: eine bestimmte Mail aus "Heute" öffnen (Muster openProfilId).
// MailScreen holt die ID beim Einhängen ab und löscht sie sofort, damit eine
// spätere Navigation die Mail nicht erneut öffnet.
let _offeneMailId: number | null = null;

export function oeffneMailVonAussen(mailId: number) {
  _offeneMailId = mailId;
  globalNav('mails');
}

export function nimmOffeneMailId(): number | null {
  const id = _offeneMailId;
  _offeneMailId = null;
  return id;
}

// Welle U2: Verfassen-Dialog im Mail-Screen mit vorbefülltem Empfänger öffnen
// (gleiches Abhol-Muster wie oeffneMailVonAussen).
let _mailEntwurf: { an: string; betreff: string } | null = null;

export function oeffneMailVerfassen(an: string, betreff = '') {
  _mailEntwurf = { an, betreff };
  globalNav('mails');
}

export function nimmMailEntwurf(): { an: string; betreff: string } | null {
  const e = _mailEntwurf;
  _mailEntwurf = null;
  return e;
}

// Welle U2: Klasse › Klasse › Aktionen einer bestimmten Klasse öffnen,
// optional mit hervorgehobener Aktion. KlasseTab holt das Ziel beim Einhängen ab.
// Welle B1: tab 'schueler' = Schülerliste dieser Klasse (oeffneKlasse, Push „Mitarbeit-Lücken“).
export type KlassenZiel = { klasseId: number | null; aktionId: number | null; tab: 'schueler' | 'klasse' };
let _klassenAktionZiel: KlassenZiel | null = null;

export function oeffneKlassenAktionen(klasseId: number | null, aktionId: number | null = null) {
  _klassenAktionZiel = { klasseId, aktionId, tab: 'klasse' };
  globalNav('klasse');
}

export function oeffneKlasse(klasseId: number | null) {
  _klassenAktionZiel = { klasseId, aktionId: null, tab: 'schueler' };
  globalNav('klasse');
}

export function nimmKlassenAktionZiel(): KlassenZiel | null {
  const z = _klassenAktionZiel;
  _klassenAktionZiel = null;
  return z;
}

// Welle U4b: Notizen-Screen mit Schülerfilter öffnen ("Alle n Notizen von X" im Profil)
// Welle U6b: notizId öffnet zusätzlich das Notiz-Sheet dieser Notiz (Suche); archiv = sie liegt im Archiv
type NotizenFilter = { schuelerId: number; klasseId?: number | null; notizId?: number | null; archiv?: boolean };
let _notizenFilter: NotizenFilter | null = null;

export function oeffneNotizen(schuelerId: number, klasseId: number | null = null, notizId: number | null = null, archiv = false) {
  _notizenFilter = { schuelerId, klasseId, notizId, archiv };
  globalNav('notizen');
}

export function nimmNotizenFilter(): NotizenFilter | null {
  const f = _notizenFilter;
  _notizenFilter = null;
  return f;
}

// ── Welle U6b ────────────────────────────────────────────────────────────────

// Zurück zur Herkunft: Sprünge aus dem Schülerprofil (Ausflug, Klassenbuch) merken
// sich Profil + Tab. merkeProfilHerkunft schreibt sie zusätzlich in den aktuellen
// History-Eintrag, damit auch die Zurück-Taste von Browser/Gerät ins Profil führt
// (App wertet ldExtra.profil im popstate aus).
export type ProfilHerkunft = { profilId: number; ziel: ProfilZiel };

export function istProfilHerkunft(h: unknown): h is ProfilHerkunft {
  return !!h && typeof h === 'object' && typeof (h as ProfilHerkunft).profilId === 'number';
}

export function merkeProfilHerkunft(h: ProfilHerkunft) {
  const st = window.history.state ?? {};
  window.history.replaceState({ ...st, ldExtra: { ...(st.ldExtra ?? {}), profil: h } }, '', window.location.pathname);
}

// Klassenbuch an einem Datum öffnen (Muster openProfilId): KlassenbuchScreen holt
// das Ziel beim Einhängen ab. stunde öffnet direkt den Eintrag dieser Stunde;
// klasseId/schuelerId wählen die passende Klasse; herkunft = Ziel von "← Zurück".
export type KlassenbuchZiel = {
  datum: string;
  stunde?: number | null;
  klasseId?: number | null;
  schuelerId?: number | null;
  herkunft?: ProfilHerkunft | null;
};
let _klassenbuchZiel: KlassenbuchZiel | null = null;

export function oeffneKlassenbuch(ziel: KlassenbuchZiel) {
  _klassenbuchZiel = ziel;
  if (ziel.herkunft) merkeProfilHerkunft(ziel.herkunft);
  globalNav('klassenbuch');
}

export function nimmKlassenbuchZiel(): KlassenbuchZiel | null {
  const z = _klassenbuchZiel;
  _klassenbuchZiel = null;
  return z;
}

// Universaldiktat direkt in einem Modus öffnen (Schnell-Diktat auf Heute).
// Öffnet nur die Bereit-Ansicht — die Aufnahme startet erst mit dem Tipp auf
// den Aufnahme-Knopf (kein automatischer Mikrofonstart).
export function oeffneDiktat(modul: DiktatModul) {
  window.dispatchEvent(new CustomEvent('ld-diktat-modul', { detail: { modul } }));
}

// ── Welle B1 ─────────────────────────────────────────────────────────────────

// Fehlzeiten einer bestimmten Klasse öffnen (Push „Überfällige Entschuldigungen“).
// FehlzeitenScreen holt die Klasse beim Einhängen ab (Muster openProfilId).
let _fehlzeitenKlasse: number | null = null;

export function oeffneFehlzeiten(klasseId: number | null) {
  _fehlzeitenKlasse = klasseId;
  globalNav('fehlzeiten');
}

export function nimmFehlzeitenKlasse(): number | null {
  const k = _fehlzeitenKlasse;
  _fehlzeitenKlasse = null;
  return k;
}

// Offene Note-Vorschläge auf Heute ausgeklappt zeigen (Push „Offene Note-Vorschläge“).
// HeuteScreen holt den Wunsch beim Einhängen ab (die App hängt den Bildschirm beim Push neu ein).
let _noteVorschlaegeZeigen = false;

export function zeigeNoteVorschlaege() {
  _noteVorschlaegeZeigen = true;
  globalNav('home');
}

export function nimmNoteVorschlaegeZeigen(): boolean {
  const z = _noteVorschlaegeZeigen;
  _noteVorschlaegeZeigen = false;
  return z;
}

// ── Welle A3: Beitrag in „Sicherheit & Tipps“ öffnen (Hinweis auf Heute, Push) ──
let _beitragZiel: number | null = null;
export function oeffneBeitrag(id: number) {
  _beitragZiel = id;
  globalNav('mitteilungen');
  window.dispatchEvent(new CustomEvent(BEITRAG_EVENT, { detail: id }));
}
export const BEITRAG_EVENT = 'ld-beitrag-oeffnen';
export function nimmBeitragZiel(): number | null {
  const z = _beitragZiel;
  _beitragZiel = null;
  return z;
}

// ── Welle B2 ─────────────────────────────────────────────────────────────────

// „Gespräch dokumentieren“ aus dem Kalender: Profil des Kindes öffnen (Bereich Gespräche),
// das Profil holt den Termin beim Einhängen ab und öffnet das vorbelegte Gesprächsfenster.
let _gespraechAusTermin: { schuelerId: number; terminId: number } | null = null;
let _profilOeffner: ((schuelerId: number, ziel: ProfilZiel) => void) | null = null;

export function registerProfilOeffner(fn: ((schuelerId: number, ziel: ProfilZiel) => void) | null) {
  _profilOeffner = fn;
}

export function oeffneGespraechAusTermin(schuelerId: number, terminId: number) {
  _gespraechAusTermin = { schuelerId, terminId };
  _profilOeffner?.(schuelerId, 'gespraeche');
}

export function nimmGespraechAusTermin(schuelerId: number): number | null {
  const z = _gespraechAusTermin;
  if (!z || z.schuelerId !== schuelerId) return null;
  _gespraechAusTermin = null;
  return z.terminId;
}

// Klassenarbeit als Liste: ein Fenster für die ganze App (KlassenarbeitListeHost), geöffnet aus
// der Aufgabe in Mein Tag, dem Kalendereintrag und dem Notenrechner — ohne Bildschirmwechsel.
export type KaListeZiel = { terminId?: number | null; klasseId?: number | null; fach?: string | null; taskId?: number | null };
export const KA_LISTE_EVENT = 'ld-klassenarbeit-liste';

export function oeffneKlassenarbeitListe(ziel: KaListeZiel) {
  window.dispatchEvent(new CustomEvent<KaListeZiel>(KA_LISTE_EVENT, { detail: ziel }));
}

// ── Welle B3 ─────────────────────────────────────────────────────────────────

// „Per Sprache nachtragen“: ein Fenster für die ganze App (KlassenbuchNachtragenHost), geöffnet
// aus dem Klassenbuch und neben „Jetzt nachtragen“ auf Heute — ohne Bildschirmwechsel.
export const NACHTRAGEN_EVENT = 'ld-klassenbuch-nachtragen';
export function oeffneKlassenbuchNachtragen() {
  window.dispatchEvent(new CustomEvent(NACHTRAGEN_EVENT));
}
// Nach dem Speichern: Klassenbuch und Heute-Karten laden neu
export const KLASSENBUCH_GEAENDERT = 'ld-klassenbuch-geaendert';

// ── Welle B4: Blatt fürs Gespräch, Konferenzliste, AV/SV-Vorschläge ──────────────
// Je ein Fenster für die ganze App (KonferenzHosts am App-Root), mehrere Einstiege.
export type BlattZiel = { schuelerId: number; name: string };
export type KonferenzZiel = { klasseId: number; klasseName: string };
export const BLATT_EVENT = 'ld-blatt-gespraech';
export const KONFERENZ_EVENT = 'ld-konferenzliste';
export const AVSV_EVENT = 'ld-avsv-vorschlaege';
export const oeffneBlatt = (z: BlattZiel) => window.dispatchEvent(new CustomEvent<BlattZiel>(BLATT_EVENT, { detail: z }));
export const oeffneKonferenzliste = (z: KonferenzZiel) => window.dispatchEvent(new CustomEvent<KonferenzZiel>(KONFERENZ_EVENT, { detail: z }));
export const oeffneAvSv = (z: KonferenzZiel & { halbjahr?: 1 | 2 }) => window.dispatchEvent(new CustomEvent(AVSV_EVENT, { detail: z }));
